import { RegularVariantsByArticleIdQuery } from '@/productDiscovery/ProductPage/common/components/VariantSelector/regularVariantQuery/generated/regularVariants.generated';
import { AttributeValueFragment } from '@/productDiscovery/ProductPage/graphql/productPageMain/generated/productPageMain.generated';

export type VariantListQL =
  RegularVariantsByArticleIdQuery['regularVariantsByArticleId']['variants'];
export type Label = string;
export type AttributeName = string;

export interface AttributeWithLabel extends AttributeValueFragment {
  label: Label;
}

export interface LabeledVariant {
  attributes: AttributeWithLabel[];
  modelId: string;
}

export interface ChipType extends AttributeWithLabel {
  distance: number;
  modelId: string;
}

export type AttributeChips = Record<Label, ChipType>;
export type VariantChips = Record<AttributeName, AttributeChips>;
export type SelectedAttributes = Record<AttributeName, AttributeWithLabel>;

export const createAttributeLabel = (
  attribute: AttributeValueFragment,
): Label =>
  attribute.unit?.abbreviation
    ? `${attribute.values?.map((value) => value.value).join(' / ')} ${attribute.unit?.abbreviation}`
    : `${attribute.values?.map((value) => value.value).join(' / ')}`;

// receives a list of variants, and adds labels to each attribute based on their values
export const parseVariantAttributes = (
  variants: VariantListQL,
): LabeledVariant[] => {
  if (!variants) return [];

  // To start, we need to add labels to every array of attributes based on their values.
  // Because we cannot trust the data received, as some of the moddels can be missing attributes, we implement a litle hack
  // we will keep track of the amount of attributes each variant has, and sort the list based on that
  // this way, we can be sure that the matching will take the maximum amount of attributes into account
  const variantList = variants.map((variant) => ({
    ...variant,
    amountOfAttributes: variant.attributes.length, // remove the field when the backend implements data validation.
    attributes: variant.attributes.map((attribute) => ({
      ...attribute,
      label: createAttributeLabel(attribute),
    })),
  }));

  return variantList;
};

export const buildChips = (
  variantList: LabeledVariant[],
  selectedVariantAttributes: AttributeWithLabel[],
): VariantChips => {
  const selectedAttributes = selectedVariantAttributes.reduce(
    (acc: SelectedAttributes, attribute) => {
      acc[attribute.name] = attribute;
      return acc;
    },
    {},
  );

  // create the top level chips state with the valid attributes as keys and empty records as values
  const variantChips: VariantChips = {};

  // finally, we iterate again over the variants array to populate the chips state
  variantList.forEach((variant) => {
    // distance tells us how "different" is a variant in comparison with the selected one
    // for current selected variant, distance will be 0
    // for a variant that only differs in one attribute, distance will be 1, and the chip will be enabled
    // for a variant that differs in 2 attributes, distance will be 2, and the chip will be disabled
    const distance = variant.attributes.reduce((dist, attribute) => {
      // if the attribute value does NOT match the selected one, we increase the distance
      return selectedAttributes[attribute.name]?.label == attribute?.label
        ? dist
        : dist + 1;
    }, 0);

    // distance is the metric to know if a chip will be "enabled/disabled"
    // finally, we iterate to create the AttributeChips, one for each different label
    variant.attributes.forEach((attribute) => {
      // if an attribute dimension does not exist, we create it
      // this will happen on the first iteration of each attribute
      if (!variantChips[attribute.name]) {
        variantChips[attribute.name] = {};
      }

      const newChip: ChipType = {
        ...attribute,
        distance,
        modelId: variant.modelId,
      };
      // we check if the chip with that label exists already
      const existingChip = variantChips[attribute.name][attribute?.label];
      // if the chip does not exist, we add ours. Easy!
      if (!existingChip) {
        variantChips[attribute.name][attribute?.label] = newChip;
        return;
      }

      // if the chip already exists, we have to compare the distance to know which one is the closest one
      // the chip must have the lowest value of distance
      variantChips[attribute.name][attribute?.label] =
        newChip.distance < existingChip.distance ? newChip : existingChip;
    });
  });

  return variantChips;
};
